/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { get, capitalize } from 'lodash';
import dayjs from 'dayjs';

import { usePersistState, formatNumber } from 'helper';
import Button from 'partial/components/Button';
import {
  X,
  CaretDown,
  CaretUp,
  ChartBar,
  ChartLine,
  Plus,
} from '@phosphor-icons/react';
import { toastWarning } from 'partial/components/react-geek-toast';
import DelayDiv from 'modules/common/components/DelayDiv';
import LineChartSeries from 'modules/common/chart/LineChartSeries';
import BarChartSeries from 'modules/common/chart/BarChartSeries';
import HistogramRange from '../components/HistogramRange';
import FormSelectStatic from '../components/FormSelectStatic';
import { useCompare } from '../hooks';

import {
  COMPARE_OPTIONS,
  MAP_GROUP,
  COLORS,
  MAP_ROWS,
  buildId,
} from '../constant';

const renderCount = (
  row,
  col,
  data,
  mode,
  displayKey,
  postfix = '',
  defaultValue = null,
) => {
  const raw = get(data, `${buildId(col, MAP_GROUP[mode])}.table.${row}`) || {};
  return raw[`${displayKey}${postfix}`] || defaultValue;
};

const renderCompare = (curr, prev, compare) => {
  if (!prev || !curr) return null;
  const difference = ((curr - prev) / prev) * 100;
  const percentage = formatNumber(difference, 1);
  if (difference === 0) return null;
  const getClassName = () => {
    if (compare && compare === dayjs().format('YYYY-MM-DD'))
      return 'py-0 px-2 badge badge-warning ml-1';
    if (difference > 0) return 'py-0 px-2 badge badge-success ml-1';
    if (difference < 0) return 'py-0 px-2 badge badge-danger ml-1';
    return 'py-0 px-2 badge badge-secondary ml-1';
  };
  const getIcon = () => {
    if (difference > 0) return <CaretUp className="h-4 w-4 inline-block" />;
    if (difference < 0) return <CaretDown className="h-4 w-4 inline-block" />;
    return null;
  };
  return (
    <span className={getClassName()}>
      {getIcon()}
      {percentage}%
    </span>
  );
};

function CompareValue({ row, col, prevCol, data, meta, prefix, defaultValue }) {
  return (
    <>
      {renderCount(
        row,
        col,
        data,
        meta.compare.value,
        meta.key,
        prefix,
        defaultValue,
      )}
      {renderCompare(
        renderCount(row, col, data, meta.compare.value, meta.key),
        renderCount(row, prevCol, data, meta.compare.value, meta.key),
      )}
    </>
  );
}

CompareValue.defaultProps = {
  prevCol: null,
  prefix: '',
  defaultValue: null,
};

CompareValue.propTypes = {
  row: PropTypes.string.isRequired,
  col: PropTypes.string.isRequired,
  prevCol: PropTypes.oneOfType([
    PropTypes.instanceOf(Object),
    PropTypes.string,
  ]),
  data: PropTypes.instanceOf(Object).isRequired,
  meta: PropTypes.instanceOf(Object).isRequired,
  prefix: PropTypes.string,
  defaultValue: PropTypes.oneOfType([
    PropTypes.instanceOf(Object),
    PropTypes.string,
  ]),
};

function Compare() {
  const [meta, setMeta] = usePersistState(
    'comparative-meta',
    { compare: { label: 'By Month', value: 'month' } },
    {
      keepOnUnmount: false,
    },
  );
  const [items, setItems] = usePersistState(
    'comparative-items',
    [
      dayjs().subtract(2, 'month').format('YYYY-MM-DD'),
      dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
      dayjs().format('YYYY-MM-DD'),
    ],
    {
      keepOnUnmount: false,
    },
  );

  const [chartType, setChartType] = useState('line');
  const [isLoading, data, chart] = useCompare(items, meta.compare.value);

  const handleAddItem = () => {
    setItems((state) => {
      const val = dayjs().format('YYYY-MM-DD');
      const ALLOW_DUPLICATE = true;
      if (items.indexOf(val) > -1 && !ALLOW_DUPLICATE) {
        toastWarning('Already Added!');
        return state;
      }
      return state.concat([val]).sort();
    });
  };
  const handleChangeItem = (index) => (v) => {
    setItems((state) => {
      const newValue = [...state].slice(0); // clone object
      newValue[index] = dayjs(v().from).format('YYYY-MM-DD');
      return newValue;
    });
  };
  const handleRemoveItem = (index) => () => {
    setItems((state) => {
      const newValue = [...state].slice(0); // clone object
      newValue.splice(index, 1);
      return newValue;
    });
  };

  const rows = useMemo(
    () => MAP_ROWS[meta.compare.value],
    [meta.compare.value],
  );
  const rowLabel = React.useMemo(
    () => MAP_GROUP[meta.compare.value],
    [meta.compare],
  );

  const toggleChart = useMemo(
    () => (
      <div className="grid grid-cols-1 sm:flex gap-1 items-center">
        <div className="space-x-1 flex justify-end">
          <Button
            size="xs"
            primary={chartType === 'line'}
            icon={ChartLine}
            onClick={() => setChartType('line')}
          />
          <Button
            size="xs"
            primary={chartType === 'bar'}
            icon={ChartBar}
            onClick={() => setChartType('bar')}
          />
        </div>
      </div>
    ),
    [chartType, setChartType],
  );

  const renderControl = () => (
    <thead className="bg-white flex-grow sticky top-0 ">
      <tr className="px-1">
        <th className="text-left font-semibold text-sm text-neutral-500 whitespace-nowrap pr-4">
          {capitalize(rowLabel)}
        </th>
        {items.map((item, i) => (
          // eslint-disable-next-line
          <th className="px-1" key={`${item}-${i}`}>
            <div
              className="min-w-min px-5 py-2 flex space-x-1 whitespace-nowrap justify-center items-center"
              style={{ backgroundColor: COLORS[i] }}
            >
              <HistogramRange
                key={meta.compare.value}
                onChange={handleChangeItem(i)}
                defaultMode={meta.compare.value}
                defaultValue={item}
                switchMode={false}
              />
              <button
                type="button"
                className="h-6 w-6 bg-white flex items-center justify-center rounded hover:bg-neutral-100 "
                onClick={handleRemoveItem(i)}
              >
                <X size={12} aria-label="Remove" />
              </button>
            </div>
          </th>
        ))}

        <th>
          <button
            type="button"
            className="ml-2 flex items-center text-neutral-500 gap-x-2 px-3 py-2 border rounded hover:bg-brand-600 hover:text-white hover:border-brand-600 transition"
            onClick={handleAddItem}
          >
            <Plus />
            <span className="font-light text-xs">Add</span>
          </button>
        </th>
      </tr>
    </thead>
  );

  return (
    <div className="space-y-6 px-4 pt-4">
      <div className="flex items-center justify-between">
        <FormSelectStatic
          name="compare"
          data={COMPARE_OPTIONS}
          onChange={setMeta}
          value={meta?.compare}
        />
        {toggleChart}
      </div>
      <DelayDiv
        delay={100}
        loader={
          <div
            className="col-span-2 bg-neutral-100 animate-pulse rounded-lg"
            style={{ height: 361 }}
          />
        }
      >
        <div className="flex flex-col pr-3" style={{ height: 250 }}>
          {chartType === 'line' && (
            <LineChartSeries
              labels={rows}
              series={chart}
              colors={COLORS}
              isLoading={isLoading}
            />
          )}
          {chartType === 'bar' && (
            <BarChartSeries
              labels={rows}
              series={chart}
              colors={COLORS}
              isLoading={isLoading}
            />
          )}
        </div>
      </DelayDiv>

      <div className="flex flex-col">
        <div className="flex-grow overflow-x-scroll border-b border-neutral-300 sm:rounded-md">
          <table className="h-full w-full relative">
            {renderControl()}
            <tbody className="bg-white gap-y-10">
              {rows?.map((row) => (
                <tr key={row}>
                  <td className="text-neutral-500 font-semibold text-xs w-[6rem] whitespace-nowrap">
                    {row}
                  </td>
                  {items.map((c, i) => (
                    <td
                      className="text-neutral-500 font-semibold text-xs text-center py-3"
                      // eslint-disable-next-line
                      key={`${row}-${i}`}
                    >
                      <CompareValue
                        row={row}
                        col={c}
                        prevCol={items[i - 1]}
                        data={data}
                        meta={meta}
                        prefix="_human"
                        defaultValue="-"
                      />
                    </td>
                  ))}
                  <td />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Compare;
